'use client';

import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { isSafeRedirectPath } from '@kit/shared/utils';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import { Trans } from '@kit/ui/trans';

import {
  type SignupCustomField,
  type SignupFieldEntry,
  validateSignupFieldEntries,
} from '../schemas/signup-custom-fields.schema';

type SignupFieldsValues = {
  signupFields?: SignupFieldEntry[];
};

export function refineSignupFields(fields: SignupCustomField[]) {
  return (values: SignupFieldsValues, context: z.RefinementCtx) => {
    const visibleFields = fields.filter((field) => field.for_parent);
    for (const error of validateSignupFieldEntries(fields, values.signupFields ?? [], 'parent')) {
      const index = visibleFields.findIndex((field) => field.id === error.fieldId);
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: error.message,
        path: ['signupFields', index],
      });
    }
  };
}

export function SignupCustomFieldsInput({
  fields,
  name = 'signupFields',
  disabled = false,
}: {
  fields: SignupCustomField[];
  name?: 'signupFields';
  disabled?: boolean;
}) {
  const { control } = useFormContext<SignupFieldsValues>();
  const visibleFields = fields.filter((field) => field.for_parent);
  const today = new Date();
  const maxDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  return visibleFields.map((definition, index) => (
    <FormField
      key={definition.id}
      control={control}
      name={`${name}.${index}`}
      defaultValue={{ fieldId: definition.id, value: { text: '' } }}
      render={({ field }) => {
        const value = field.value?.value;
        const current = value ? Object.values(value)[0] : '';
        const onChange = (raw: string) => {
          let value: SignupFieldEntry['value'] = { text: raw };
          if (raw !== '') {
            if (definition.field_type === 'date') value = { date: raw };
            if (definition.field_type === 'number')
              value = { number: Number(raw) };
            if (definition.field_type === 'select') value = { select: raw };
            if (definition.field_type === 'boolean')
              value = { boolean: raw === 'true' };
          }
          field.onChange({ fieldId: definition.id, value });
        };
        return (
          <FormItem>
            <FormLabel>
              {definition.label}
              {definition.is_required && (
                <span className="text-muted-foreground ms-2 text-xs">
                  <Trans
                    i18nKey="auth:signupFieldRequired"
                    defaults="Required"
                  />
                </span>
              )}
            </FormLabel>
            <FormControl>
              {definition.field_type === 'select' ||
              definition.field_type === 'boolean' ? (
                <select
                  ref={field.ref}
                  name={field.name}
                  onBlur={field.onBlur}
                  value={String(current ?? '')}
                  onChange={(event) => onChange(event.target.value)}
                  disabled={disabled}
                  required={definition.is_required}
                  data-test={`signup-custom-field-${definition.id}`}
                  className="border-input bg-background h-10 w-full rounded-md border px-3 text-sm"
                >
                  <option value="">
                    <Trans
                      i18nKey="auth:signupFieldChoose"
                      defaults="Select an answer"
                    />
                  </option>
                  {definition.field_type === 'boolean' ? (
                    <>
                      <option value="true">
                        <Trans i18nKey="common:yes" defaults="Yes" />
                      </option>
                      <option value="false">
                        <Trans i18nKey="common:no" defaults="No" />
                      </option>
                    </>
                  ) : (
                    definition.options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))
                  )}
                </select>
              ) : (
                <Input
                  ref={field.ref}
                  name={field.name}
                  onBlur={field.onBlur}
                  value={typeof current === 'boolean' ? '' : (current ?? '')}
                  onChange={(event) => onChange(event.target.value)}
                  disabled={disabled}
                  required={definition.is_required}
                  type={
                    definition.field_type === 'date'
                      ? 'date'
                      : definition.field_type === 'number'
                        ? 'number'
                        : 'text'
                  }
                  step={definition.field_type === 'number' ? 'any' : undefined}
                  min={
                    definition.field_type === 'number'
                      ? (definition.min_value ?? undefined)
                      : undefined
                  }
                  max={
                    definition.is_date_of_birth
                      ? maxDate
                      : definition.field_type === 'number'
                        ? (definition.max_value ?? undefined)
                        : undefined
                  }
                  autoComplete={
                    definition.is_date_of_birth ? 'bday' : undefined
                  }
                  data-test={`signup-custom-field-${definition.id}`}
                />
              )}
            </FormControl>
            {definition.description && (
              <FormDescription>{definition.description}</FormDescription>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  ));
}

export function signupProfileSetupPath(next?: string | null) {
  const safe =
    next && isSafeRedirectPath(next) && !next.startsWith('/auth/profile-setup');
  return safe && next
    ? `/auth/profile-setup?next=${encodeURIComponent(next)}`
    : '/auth/profile-setup';
}
